import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db, storage } from '../../firebase/config';
import { collection, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import toast from 'react-hot-toast';
import './SuperAdminPanel.css';

const SuperAdminPanel = () => {
  const { superAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!superAdmin) { navigate('/superadmin/login'); return; }
    fetchBrands();
  }, [superAdmin, navigate]);

  const fetchBrands = async () => {
    try {
      const snap = await getDocs(collection(db, 'brands'));
      setBrands(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch { setBrands([]); }
    setLoading(false);
  };

  const handleDelete = async (brand) => {
    if (!window.confirm(`Delete brand "${brand.name}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'brands', brand.id));
      if (brand.logoStoragePath) { try { await deleteObject(ref(storage, brand.logoStoragePath)); } catch {} }
      toast.success('Brand deleted');
      fetchBrands();
    } catch { toast.error('Delete failed'); }
  };

  const handleToggleActive = async (brand) => {
    try {
      await updateDoc(doc(db, 'brands', brand.id), { active: !brand.active });
      toast.success(brand.active ? 'Brand deactivated' : 'Brand activated');
      fetchBrands();
    } catch { toast.error('Update failed'); }
  };

  if (!superAdmin) return null;

  return (
    <div className="sap-page">
      <div className="sap-sidebar">
        <div className="sap-logo">
          <div className="sap-logo-icon">CC</div>
          <div>
            <div className="sap-logo-name">CateringCo</div>
            <div className="sap-logo-sub">Super Admin</div>
          </div>
        </div>
        <nav className="sap-nav">
          <button className="sap-nav-btn active"><span>🏢</span>All Brands</button>
        </nav>
        <div className="sap-sidebar-footer">
          <a href="/" target="_blank" rel="noreferrer" className="sap-view-site">↗ View Platform</a>
          <button className="sap-logout" onClick={() => { logout(); navigate('/'); }}>Sign Out</button>
        </div>
      </div>

      <div className="sap-content">
        <div className="sap-section">
          <div className="sap-section-header">
            <div>
              <h2>All Brands</h2>
              <p>{brands.length} brand{brands.length !== 1 ? 's' : ''} on platform</p>
            </div>
            <button className="btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>+ Add Brand</button>
          </div>

          {showForm && (
            <BrandForm
              editing={editing}
              onClose={() => { setShowForm(false); setEditing(null); }}
              onSaved={() => { fetchBrands(); setShowForm(false); setEditing(null); }}
            />
          )}

          {loading ? <p style={{ color: 'var(--grey-mid)' }}>Loading...</p> : (
            <div className="sap-brands-list">
              {brands.map(brand => (
                <div key={brand.id} className={`sap-brand-card ${!brand.active ? 'inactive' : ''}`}>
                  <div className="sap-brand-logo" style={{ background: brand.primaryColor || '#111' }}>
                    {brand.logoUrl ? <img src={brand.logoUrl} alt={brand.name} /> : <span>{brand.name?.slice(0,2).toUpperCase()}</span>}
                  </div>
                  <div className="sap-brand-info">
                    <div className="sap-brand-top">
                      <h3>{brand.name}</h3>
                      <span className={`sap-brand-status ${brand.active ? 'active' : 'inactive'}`}>{brand.active ? 'Active' : 'Inactive'}</span>
                    </div>
                    <p className="sap-brand-slug">cateringco.com.au/<strong>{brand.id}</strong></p>
                    <p className="sap-brand-tagline">{brand.tagline}</p>
                  </div>
                  <div className="sap-brand-actions">
                    <a href={`/${brand.id}`} target="_blank" rel="noreferrer" className="sap-action-btn">View ↗</a>
                    <a href={`/${brand.id}/admin`} target="_blank" rel="noreferrer" className="sap-action-btn">Admin ↗</a>
                    <button className="btn-outline" style={{ padding: '6px 12px', fontSize: 11 }} onClick={() => { setEditing(brand); setShowForm(true); }}>Edit</button>
                    <button className="sap-toggle-btn" onClick={() => handleToggleActive(brand)}>{brand.active ? 'Deactivate' : 'Activate'}</button>
                    <button className="btn-danger" onClick={() => handleDelete(brand)}>Delete</button>
                  </div>
                </div>
              ))}
              {brands.length === 0 && <div className="sap-empty"><h3>NO BRANDS YET</h3><p>Add your first brand to get started!</p></div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BrandForm = ({ editing, onClose, onSaved }) => {
  const fileRef = useRef();
  const [form, setForm] = useState({
    name: editing?.name || '',
    slug: editing?.id || '',
    tagline: editing?.tagline || '',
    primaryColor: editing?.primaryColor || '#111111',
    secondaryColor: editing?.secondaryColor || '#ffffff',
    adminPassword: editing?.adminPassword || '',
    active: editing?.active ?? true,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(editing?.logoUrl || null);
  const [saving, setSaving] = useState(false);

  const handleFile = (e) => { const f = e.target.files[0]; if (!f) return; setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Brand name required'); return; }
    if (!editing && !form.slug.trim()) { toast.error('URL slug required'); return; }
    if (!editing && !form.adminPassword.trim()) { toast.error('Admin password required'); return; }
    const slug = form.slug.toLowerCase().replace(/[^a-z0-9]/g, '');
    setSaving(true);
    try {
      let logoUrl = editing?.logoUrl || '';
      let logoStoragePath = editing?.logoStoragePath || '';
      if (logoFile) {
        const path = `brands/${slug}/logo/${Date.now()}_${logoFile.name}`;
        const sRef = ref(storage, path);
        await uploadBytes(sRef, logoFile);
        logoUrl = await getDownloadURL(sRef);
        logoStoragePath = path;
        if (editing?.logoStoragePath) { try { await deleteObject(ref(storage, editing.logoStoragePath)); } catch {} }
      }
      const data = { name: form.name, tagline: form.tagline, primaryColor: form.primaryColor, secondaryColor: form.secondaryColor, adminPassword: form.adminPassword || editing?.adminPassword, active: form.active, logoUrl, logoStoragePath };
      if (editing) {
        await updateDoc(doc(db, 'brands', editing.id), data);
        toast.success('Brand updated!');
      } else {
        const { setDoc: setDocFn } = await import('firebase/firestore');
        const { doc: docFn } = await import('firebase/firestore');
        await setDocFn(docFn(db, 'brands', slug), data);
        toast.success('Brand created!');
      }
      onSaved();
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  };

  return (
    <div className="sap-form-card">
      <div className="sap-form-header"><h3>{editing ? `Edit ${editing.name}` : 'Add New Brand'}</h3><button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--grey-mid)' }}>×</button></div>
      <div className="sap-form-grid">
        <div className="form-group"><label className="form-label">Brand Name *</label><input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sandwich Chefs" /></div>
        {!editing && <div className="form-group"><label className="form-label">URL Slug *</label><input className="form-input" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })} placeholder="e.g. sandwichchefs" /><p style={{ fontSize: 11, color: 'var(--grey-mid)', marginTop: 5 }}>cateringco.com.au/<strong>{form.slug || 'slug'}</strong></p></div>}
        <div className="form-group" style={{ gridColumn: editing ? '1/-1' : '' }}><label className="form-label">Tagline</label><input className="form-input" value={form.tagline} onChange={e => setForm({ ...form, tagline: e.target.value })} placeholder="e.g. Fresh · Gourmet · Made to Order" /></div>
        <div className="form-group"><label className="form-label">Primary Colour</label><div style={{ display: 'flex', gap: 10, alignItems: 'center' }}><input type="color" value={form.primaryColor} onChange={e => setForm({ ...form, primaryColor: e.target.value })} style={{ width: 48, height: 40, border: 'none', padding: 2, cursor: 'pointer', borderRadius: 4 }} /><input className="form-input" value={form.primaryColor} onChange={e => setForm({ ...form, primaryColor: e.target.value })} placeholder="#111111" /></div></div>
        <div className="form-group"><label className="form-label">Secondary Colour</label><div style={{ display: 'flex', gap: 10, alignItems: 'center' }}><input type="color" value={form.secondaryColor} onChange={e => setForm({ ...form, secondaryColor: e.target.value })} style={{ width: 48, height: 40, border: 'none', padding: 2, cursor: 'pointer', borderRadius: 4 }} /><input className="form-input" value={form.secondaryColor} onChange={e => setForm({ ...form, secondaryColor: e.target.value })} placeholder="#ffffff" /></div></div>
        <div className="form-group"><label className="form-label">{editing ? 'New Admin Password' : 'Admin Password *'}</label><input className="form-input" type="password" value={form.adminPassword} onChange={e => setForm({ ...form, adminPassword: e.target.value })} placeholder={editing ? 'Leave blank to keep current' : 'Set admin password'} /></div>
        <div className="form-group"><label className="form-label">Logo</label><input type="file" accept="image/*" ref={fileRef} onChange={handleFile} style={{ display: 'none' }} /><button className="btn-outline" onClick={() => fileRef.current.click()} style={{ width: '100%' }}>{logoPreview ? '↻ Change Logo' : '↑ Upload Logo'}</button>{logoPreview && <img src={logoPreview} alt="logo preview" style={{ width: '100%', height: 80, objectFit: 'contain', marginTop: 10, background: form.primaryColor, borderRadius: 6, padding: 8 }} />}</div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 24 }}><input type="checkbox" id="active" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} style={{ width: 16, height: 16 }} /><label htmlFor="active" style={{ fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Active (visible on homepage)</label></div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update Brand' : 'Create Brand'}</button>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
};

export default SuperAdminPanel;
